import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, BarChart3, Table2, ChevronRight, Minus } from 'lucide-react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import axios from 'axios';

// Use the same API pattern as every other component in the app
const API = '/api';

function MiniChart({ chart }) {
  if (!chart || chart.type === 'none') return null;
  
  const colors = ['#6366f1','#f59e0b','#10b981','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316','#06b6d4','#84cc16'];
  
  const data = {
    labels: chart.labels || [],
    datasets: (chart.datasets || []).map((ds, i) => ({
      label: ds.label || '',
      data: ds.data || [],
      backgroundColor: chart.type === 'pie' ? colors : colors[i % colors.length] + '99',
      borderColor: chart.type === 'pie' ? '#1e1e2e' : colors[i % colors.length],
      borderWidth: chart.type === 'pie' ? 2 : 2,
      tension: 0.3,
      fill: chart.type === 'line',
    })),
  };
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: data.datasets.length > 1, labels: { color: '#94a3b8', font: { size: 10 } } },
      title: { display: !!chart.title, text: chart.title, color: '#e2e8f0', font: { size: 12 } } },
    scales: chart.type !== 'pie' ? {
      x: { ticks: { color: '#64748b', font: { size: 9 }, maxRotation: 45 }, grid: { color: '#1e293b' } },
      y: { ticks: { color: '#64748b', font: { size: 9 } }, grid: { color: '#1e293b' } }
    } : undefined,
  };
  
  const ChartComponent = chart.type === 'line' ? Line : chart.type === 'pie' ? Pie : Bar;
  return (
    <div data-testid="chat-chart" className="mt-3 bg-slate-900/50 rounded-lg p-3" style={{ height: 200 }}>
      <ChartComponent data={data} options={options} />
    </div>
  );
}

function MiniTable({ table }) {
  if (!table || !table.headers || !table.rows?.length) return null;
  return (
    <div data-testid="chat-table" className="mt-3 overflow-x-auto rounded-lg border border-slate-700/50">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-800/80">
            {table.headers.map((h, i) => (
              <th key={i} className="px-3 py-2 text-left text-slate-300 font-medium whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.slice(0, 15).map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-slate-900/30' : 'bg-slate-800/20'}>
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-1.5 text-slate-400 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[85%] ${isUser ? 'bg-indigo-600 text-white rounded-2xl rounded-br-md px-4 py-2.5' : ''}`}>
        {!isUser && (
          <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl rounded-bl-md px-4 py-2.5 border border-slate-700/30">
            <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{msg.content}</p>
            {msg.table && <MiniTable table={msg.table} />}
            {msg.chart && <MiniChart chart={msg.chart} />}
          </div>
        )}
        {isUser && <p className="text-sm leading-relaxed">{msg.content}</p>}
      </div>
    </div>
  );
}

export default function ChatWidget({ userRole, userId }) {
  const [viewState, setViewState] = useState('closed'); // 'closed' | 'minimized' | 'open'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [hasAccess, setHasAccess] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(scrollToBottom, [messages]);
  useEffect(() => { if (viewState === 'open') inputRef.current?.focus(); }, [viewState]);
  
  // Check chat access — uses axios with withCredentials (same as all other components)
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await axios.get(`${API}/chat/check-access`, { withCredentials: true });
        setHasAccess(res.data.has_access);
      } catch {
        setHasAccess(false);
      }
    };
    checkAccess();
  }, [userRole, userId]);
  
  const fetchSuggestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/suggestions`, { withCredentials: true });
      setSuggestions(res.data.suggestions || []);
    } catch { /* silent */ }
  }, []);
  
  useEffect(() => { if (viewState === 'open' && suggestions.length === 0) fetchSuggestions(); }, [viewState, fetchSuggestions, suggestions.length]);
  
  // Only show if user has access
  if (!hasAccess) return null;
  
  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    
    const userMsg = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setSuggestions([]);
    
    try {
      const sessionMessages = [...messages, userMsg].slice(-10).map(m => ({
        role: m.role, content: m.content
      }));
      
      const res = await axios.post(`${API}/chat/message`, {
        message: text.trim(),
        session_messages: sessionMessages
      }, { withCredentials: true });
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.message || 'No response.',
        table: res.data.table,
        chart: res.data.chart,
      }]);
      if (res.data.suggestions?.length) setSuggestions(res.data.suggestions);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };
  
  return (
    <>
      {/* Floating Button — shown when fully closed */}
      {viewState === 'closed' && (
        <button
          data-testid="chat-open-btn"
          onClick={() => setViewState('open')}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full shadow-lg shadow-indigo-500/25 flex items-center justify-center transition-all hover:scale-110"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Minimized Bar */}
      {viewState === 'minimized' && (
        <button
          data-testid="chat-minimized-bar"
          onClick={() => setViewState('open')}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full shadow-lg shadow-indigo-500/25 transition-all hover:scale-[1.02]"
        >
          <BarChart3 size={16} />
          <span className="text-sm font-medium">Data Assistant</span>
          {messages.length > 0 && (
            <span className="bg-white/20 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{messages.length}</span>
          )}
        </button>
      )}
      
      {/* Chat Panel — shown when open */}
      {viewState === 'open' && (
        <div data-testid="chat-panel" className="fixed bottom-6 right-6 z-50 w-[400px] h-[560px] bg-slate-950 border border-slate-700/50 rounded-2xl shadow-2xl shadow-black/40 flex flex-col overflow-hidden">
          
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-700 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <BarChart3 size={16} className="text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Data Assistant</h3>
                <p className="text-indigo-200 text-xs">Ask anything about your data</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="chat-minimize-btn" onClick={() => setViewState('minimized')} className="text-white/70 hover:text-white p-1.5 hover:bg-white/10 rounded-lg transition-colors" title="Minimize">
                <Minus size={16} />
              </button>
              <button data-testid="chat-close-btn" onClick={() => setViewState('closed')} className="text-white/70 hover:text-white p-1.5 hover:bg-white/10 rounded-lg transition-colors" title="Close">
                <X size={16} />
              </button>
            </div>
          </div>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1 scrollbar-thin scrollbar-thumb-slate-700">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MessageCircle size={24} className="text-indigo-400" />
                </div>
                <p className="text-slate-400 text-sm mb-1">Ask me anything about your data</p>
                <p className="text-slate-500 text-xs">I can analyze leads, employees, dealers, and more</p>
              </div>
            )}
            
            {messages.map((msg, i) => <ChatMessage key={i} msg={msg} />)}
            
            {loading && (
              <div className="flex justify-start mb-3">
                <div className="bg-slate-800/60 rounded-2xl rounded-bl-md px-4 py-3 border border-slate-700/30">
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Loader2 size={14} className="animate-spin" />
                    <span>Analyzing your data...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Suggestions */}
          {suggestions.length > 0 && !loading && (
            <div data-testid="chat-suggestions" className="px-4 pb-2 shrink-0">
              <div className="flex flex-wrap gap-1.5">
                {suggestions.slice(0, 3).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="text-xs px-3 py-1.5 bg-slate-800/80 text-slate-300 rounded-full border border-slate-700/50 hover:border-indigo-500/50 hover:text-indigo-300 transition-colors truncate max-w-[95%]"
                  >
                    <ChevronRight size={10} className="inline mr-1" />{s}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Input */}
          <div className="px-4 pb-4 pt-2 shrink-0">
            <div className="flex items-center gap-2 bg-slate-800/80 rounded-xl border border-slate-700/50 focus-within:border-indigo-500/50 px-3 py-2">
              <input
                ref={inputRef}
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your data..."
                disabled={loading}
                className="flex-1 bg-transparent text-sm text-slate-200 placeholder-slate-500 outline-none"
              />
              <button
                data-testid="chat-send-btn"
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading}
                className="p-1.5 rounded-lg bg-indigo-600 text-white disabled:opacity-30 hover:bg-indigo-500 transition-colors"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
